import * as api from '@apis/discount-coupon';
import { DiscountCouponCreateModal } from '@components/discount-coupon/DiscountCouponCreateModal';
import { ApplicabilityDetailModal } from '@components/discount-coupon/ApplicabilityDetailModal';
import { useAuthentication } from '@hooks/authentication';
import { DiscountCoupon } from '@models/discount-coupon';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useDiscountCouponsStore } from '@stores/discountCouponsStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, DatePicker, Input, Modal, Select, Switch, Table } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { clearEmptyKeysFromObject } from '@utils/common';

export const DISCOUNT_TYPE_TEXT = {
  percentage: '퍼센트 할인',
  amount: '고정 금액 할인',
  unit: '구매수량 당 할인',
};

const ISSUANCE_TYPE_TEXT = {
  download: '다운로드',
  direct: '직접 발급',
};

const APPLICABILITY_TYPE_TEXT = {
  all: '전체 상품',
  products: '상품 목록',
  collections: '기획전 목록',
};

export const DiscountCouponsPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [discountTypeFilter, setDiscountTypeFilter] = useState<string>();
  const [issuanceTypeFilter, setIssuanceTypeFilter] = useState<string>();
  const searchInputRef = useRef<any>();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showApplicabilityDetailModal, setShowApplicabilityDetailModal] = useState<boolean>(false);
  const [selectedCouponForDetail, setSelectedCouponForDetail] = useState<DiscountCoupon | undefined>();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingCoupon, setEditingCoupon] = useState<DiscountCoupon>();
  const [changedValidUntil, setChangedValidUntil] = useState<string>();
  const [editValidUntilData, setEditValidUntilData] = useState<DiscountCoupon>();
  const [changedIssueEndDate, setChangedIssueEndDate] = useState<string>();
  const [editIssueEndDateData, setEditIssueEndDateData] = useState<DiscountCoupon>();

  const store = useDiscountCouponsStore();
  const dialog = useOkCancelDialog();
  useAuthentication();

  const couponsQuery = useQuery({
    queryKey: [`discount-coupons`, JSON.stringify(store.searchQuery)],
    queryFn: () => api.searchDiscountCoupons(store.searchQuery),
  });

  const updateDiscountCouponMutation = useMutation({
    mutationFn: api.updateDiscountCoupon,
    onSuccess: async () => {
      setChangedValidUntil(undefined);
      setChangedIssueEndDate(undefined);
      dialog.open({
        content: '수정했습니다.',
        type: 'ok',
      });
      couponsQuery.refetch();
    },
    onError: async (e: any) => {
      setChangedValidUntil(undefined);
      setChangedIssueEndDate(undefined);
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message,
        type: 'ok',
      });
    },
  });

  const createDiscountCouponMutation = useMutation({
    mutationFn: api.createDiscountCoupon,
    onSuccess: async () => {
      dialog.open({
        content: '쿠폰 복제가 완료되었습니다.',
        type: 'ok',
      });
      couponsQuery.refetch();
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message || '쿠폰 복제에 실패했습니다.',
        type: 'ok',
      });
    },
  });

  const deleteDiscountCouponMutation = useMutation({
    mutationFn: api.deleteDiscountCoupon,
    onSuccess: async () => {
      dialog.open({
        content: '삭제 되었습니다.',
        type: 'ok',
      });
      couponsQuery.refetch();
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message || '삭제에 실패했습니다.',
        type: 'ok',
      });
    },
  });

  useEffect(() => {
    const searchResult = couponsQuery.data?.data;
    if (searchResult) {
      store.setDiscountCouponsContainer(searchResult);
    }
  }, [couponsQuery.data?.data]);

  const TABLE_COLUMNS: ColumnsType<DiscountCoupon> = [
    {
      title: 'ID',
      dataIndex: '_id',
      width: '80px',
      fixed: 'left' as const,
      render: (_id) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(_id);
                toast.dark('ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              ID
            </Button>
          </div>
        );
      },
    },
    {
      title: '쿠폰명',
      dataIndex: 'name',
      width: '150px',
      fixed: 'left' as const,
    },
    {
      title: '설명',
      dataIndex: 'description',
      width: '200px',
    },
    {
      title: '할인 정보',
      width: '150px',
      render: (record: DiscountCoupon) => {
        const discountType = DISCOUNT_TYPE_TEXT[record.discountType as keyof typeof DISCOUNT_TYPE_TEXT];
        let discountInfo = '';
        if (record.discountType === 'percentage') {
          discountInfo = `•${record?.discountPercent || '-'}% (최대 ${(
            record.maxDiscountAmount || 0
          ).toLocaleString()}원)`;
        } else if (record.discountType === 'unit') {
          discountInfo = `개당 ${record?.discountAmountPerUnit?.toLocaleString()}원\n(최대 ${
            record.discountUnitLimit
          }개)`;
        } else {
          discountInfo = `${(record.discountAmount || 0).toLocaleString()}원`;
        }
        if (record.minPurchaseAmount) {
          discountInfo += `\n•최소주문금액: ${record.minPurchaseAmount.toLocaleString()}원`;
        }
        return (
          <div>
            <div>[{discountType}]</div>
            <div style={{ fontWeight: 'bold' }}>{discountInfo}</div>
          </div>
        );
      },
    },
    {
      title: '발급 방식',
      dataIndex: 'issuanceType',
      width: '100px',
      render: (issuanceType) => ISSUANCE_TYPE_TEXT[issuanceType as keyof typeof ISSUANCE_TYPE_TEXT],
    },
    {
      title: '사용 횟수 / 발급 수량',
      width: '150px',
      render: (record: DiscountCoupon) => {
        return (
          <div>
            <div>
              사용: {record.usedCount || 0} / {record.usageLimit || 0}
            </div>
            <div>
              발급: {record.issuedCount || 0} / {record.quantity}
            </div>
          </div>
        );
      },
    },
    {
      title: '발급 가능 기간',
      width: '200px',
      render: (record) => {
        return (
          <div>
            <div>{record.issueStartDate ? moment(record.issueStartDate).format('YYYY-MM-DD HH:mm') : '-'}</div>
            <div>{record.issueEndDate ? `~ ${moment(record.issueEndDate).format('YYYY-MM-DD HH:mm')}` : ''}</div>
            <div>
              <Button
                onClick={() => {
                  setEditIssueEndDateData(record);
                  setChangedIssueEndDate(moment(record.issueEndDate).format('YYYY-MM-DD HH:mm'));
                }}>
                발급 종료일 변경
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      title: '사용 가능 기간',
      width: '200px',
      render: (record) => {
        const validMode = record.validMode;
        if (validMode === 'midnight') {
          return '발급 당일 자정까지';
        }
        if (validMode === '24hours') {
          return '발급 시점으로부터 24시간';
        }
        return (
          <div>
            <div>{record.validFrom ? moment(record.validFrom).format('YYYY-MM-DD HH:mm') : '-'}</div>
            <div>{record.validUntil ? `~ ${moment(record.validUntil).format('YYYY-MM-DD HH:mm')}` : ''}</div>
            <div>
              <Button
                onClick={() => {
                  setEditValidUntilData(record);
                  setChangedValidUntil(moment(record.validUntil).format('YYYY-MM-DD HH:mm'));
                }}>
                사용 종료일 변경
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      title: '적용 범위',
      dataIndex: ['applicability', 'type'],
      align: 'center',
      width: '200px',
      render: (type, record) => {
        const typeText = APPLICABILITY_TYPE_TEXT[type as keyof typeof APPLICABILITY_TYPE_TEXT];
        const appOnly = record?.appOnly || false;
        const appOnlyBadge = appOnly ? (
          <div className="badge" style={{ backgroundColor: '#FF3D8F' }}>
            앱 전용
          </div>
        ) : null;

        const hasProducts = record?.applicability?.productIds && record.applicability.productIds.length > 0;
        const hasCollections = record?.applicability?.collections && record.applicability.collections.length > 0;

        if (type === 'all') {
          return (
            <div>
              {appOnlyBadge}
              {typeText}
            </div>
          );
        }

        if (hasProducts && hasCollections) {
          return (
            <div>
              {appOnlyBadge}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => {
                    setSelectedCouponForDetail({
                      ...record,
                      applicability: { ...record.applicability, type: 'products' },
                    });
                    setShowApplicabilityDetailModal(true);
                  }}>
                  상품 목록 ({record.applicability.productIds.length}개)
                </Button>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => {
                    setSelectedCouponForDetail({
                      ...record,
                      applicability: { ...record.applicability, type: 'collections' },
                    });
                    setShowApplicabilityDetailModal(true);
                  }}>
                  기획전 목록 ({record.applicability.collections.length}개)
                </Button>
              </div>
            </div>
          );
        }

        if (hasProducts) {
          return (
            <div>
              {appOnlyBadge}
              <span
                style={{
                  color: '#1890ff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => {
                  setSelectedCouponForDetail(record);
                  setShowApplicabilityDetailModal(true);
                }}>
                상품 목록 ({record.applicability.productIds.length}개)
              </span>
            </div>
          );
        }

        if (hasCollections) {
          return (
            <div>
              {appOnlyBadge}
              <span
                style={{
                  color: '#1890ff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={() => {
                  setSelectedCouponForDetail(record);
                  setShowApplicabilityDetailModal(true);
                }}>
                기획전 목록 ({record.applicability.collections.length}개)
              </span>
            </div>
          );
        }

        return (
          <div>
            {appOnlyBadge}
            {typeText}
          </div>
        );
      },
    },
    {
      title: '중복 사용',
      dataIndex: 'isStackable',
      width: '100px',
      render: (isStackable) => (isStackable ? '가능' : '불가능'),
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: '100px',
      render: (status, record: DiscountCoupon) => {
        if (moment(record.validUntil).diff(new Date()) < 0) {
          return (
            <div className="badge" style={{ backgroundColor: '#6e6e6e' }}>
              만료
            </div>
          );
        }

        if (status === 'active') {
          return (
            <div className="badge" style={{ backgroundColor: '#3fb547' }}>
              활성
            </div>
          );
        }
        return (
          <div className="badge" style={{ backgroundColor: '#bd334a' }}>
            비활성
          </div>
        );
      },
    },
    {
      title: '상태 변경',
      width: '120px',
      render: (record: DiscountCoupon) => {
        if (moment(record.validUntil).diff(new Date()) < 0) {
          return '-';
        }

        return (
          <Switch
            checked={record.status === 'active'}
            onChange={(checked) => {
              updateDiscountCouponMutation.mutate({
                couponId: record._id,
                body: { status: checked ? 'active' : 'inactive' } as any,
              });
            }}></Switch>
        );
      },
    },
    {
      title: '생성 / 수정',
      width: '200px',
      render: (record) => {
        return (
          <div>
            <div>{moment(record.createdAt).format('YYYY-MM-DD HH:mm')}</div>
            <div>{record.updatedAt ? moment(record.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}</div>
          </div>
        );
      },
    },
    {
      title: '수정',
      width: '120px',
      fixed: 'right' as const,
      render: (record) => {
        return (
          <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                setEditingCoupon(record);
                setShowEditModal(true);
              }}>
              수정
            </Button>
            <Button
              size="small"
              onClick={() => {
                api.getDiscountCoupon(record._id).then((response) => {
                  const duplicatedCoupon = {
                    name: `[복제됨] ${response.name}`,
                    description: response.description,
                    status: 'inactive',
                    issuanceType: response.issuanceType,
                    discountType: response.discountType,
                    discountAmount: response.discountAmount,
                    discountPercent: response.discountPercent,
                    maxDiscountAmount: response.maxDiscountAmount,
                    minPurchaseAmount: response.minPurchaseAmount,
                    isStackable: response.isStackable,
                    usageLimitPerUser: response.usageLimitPerUser,
                    issueLimitPerUser: response.issueLimitPerUser,
                    usageLimit: response.usageLimit,
                    quantity: response.quantity,
                    issueStartDate: response.issueStartDate,
                    issueEndDate: response.issueEndDate,
                    validFrom: response.validFrom || undefined,
                    validUntil: response.validUntil || undefined,
                    validMode: response.validMode,
                    appOnly: response.appOnly,
                    applicability: response.applicability,
                    issuedBy: response.issuedBy,
                    costBearer: response.costBearer,
                    precautions: response.precautions,
                  };
                  createDiscountCouponMutation.mutate(clearEmptyKeysFromObject(duplicatedCoupon) as any);
                });
              }}>
              복제
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                dialog.open({
                  title: '쿠폰 삭제',
                  content: '이 쿠폰을 삭제하시겠습니까?',
                  type: 'ok_cancel',
                  onConfirm: () => deleteDiscountCouponMutation.mutate(record._id),
                });
              }}>
              삭제
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <DiscountCouponCreateModal
        open={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setEditingCoupon(undefined);
        }}
        needRefetch={() => {
          couponsQuery.refetch();
        }}
        coupon={editingCoupon}></DiscountCouponCreateModal>

      <ApplicabilityDetailModal
        open={showApplicabilityDetailModal}
        onClose={() => {
          setShowApplicabilityDetailModal(false);
          setSelectedCouponForDetail(undefined);
        }}
        coupon={selectedCouponForDetail}></ApplicabilityDetailModal>

      <Modal
        footer={
          <div>
            <Button>취소</Button>
            <Button
              onClick={() => {
                if (editValidUntilData) {
                  updateDiscountCouponMutation.mutate({
                    couponId: editValidUntilData._id,
                    body: { validUntil: moment(changedValidUntil, 'YYYY-MM-DD HH:mm:00').toDate() } as any,
                  });
                }
              }}>
              저장
            </Button>
          </div>
        }
        open={!!editValidUntilData}
        onCancel={() => {
          setEditValidUntilData(undefined);
        }}>
        <div style={{ marginTop: '28px' }}>
          <div>사용 종료일 변경</div>
          <DatePicker
            value={dayjs(changedValidUntil)}
            showTime={{ showSecond: false }}
            onChange={(value, dateString) => {
              setChangedValidUntil(dateString);
            }}
          />
        </div>
      </Modal>

      <Modal
        footer={
          <div>
            <Button>취소</Button>
            <Button
              onClick={() => {
                if (editIssueEndDateData) {
                  updateDiscountCouponMutation.mutate({
                    couponId: editIssueEndDateData._id,
                    body: { issueEndDate: moment(changedIssueEndDate, 'YYYY-MM-DD HH:mm:00').toDate() } as any,
                  });
                }
              }}>
              저장
            </Button>
          </div>
        }
        open={!!editIssueEndDateData}
        onCancel={() => {
          setEditIssueEndDateData(undefined);
        }}>
        <div style={{ marginTop: '28px' }}>
          <div>발급 종료일 변경</div>
          <DatePicker
            value={dayjs(changedIssueEndDate)}
            showTime={{ showSecond: false }}
            onChange={(value, dateString) => {
              setChangedIssueEndDate(dateString);
            }}
          />
        </div>
      </Modal>

      <div className="page-header">
        <div>할인 쿠폰 관리</div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              width: '100%',
              height: '52px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
            <Input
              className={filterActiveClassName(!!store.searchQuery.keyword)}
              ref={searchInputRef}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  store.updateSearchQuery({ keyword: searchKeyword });
                }
              }}></Input>

            <FilterSelect
              style={{ width: '120px' }}
              placeholder="상태"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                store.updateSearchQuery({ status: value });
              }}
              allowClear>
              <Select.Option value="active">활성</Select.Option>
              <Select.Option value="inactive">비활성</Select.Option>
              <Select.Option value="expired">만료</Select.Option>
            </FilterSelect>

            <FilterSelect
              style={{ width: '140px' }}
              placeholder="할인 타입"
              value={discountTypeFilter}
              onChange={(value) => {
                setDiscountTypeFilter(value);
                store.updateSearchQuery({ discountType: value });
              }}
              allowClear>
              <Select.Option value="percentage">퍼센트 할인</Select.Option>
              <Select.Option value="amount">고정 금액 할인</Select.Option>
              <Select.Option value="unit">구매수량 당 할인</Select.Option>
            </FilterSelect>

            <FilterSelect
              style={{ width: '120px' }}
              placeholder="발급 방식"
              value={issuanceTypeFilter}
              onChange={(value) => {
                setIssuanceTypeFilter(value);
                store.updateSearchQuery({ issuanceType: value });
              }}
              allowClear>
              <Select.Option value="DOWNLOAD">다운로드</Select.Option>
              <Select.Option value="DIRECT">직접 발급</Select.Option>
            </FilterSelect>

            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  setStatusFilter(undefined);
                  setDiscountTypeFilter(undefined);
                  setIssuanceTypeFilter(undefined);
                  store.resetSearchQuery();
                }}>
                검색 초기화
              </Button>
              <Button
                onClick={() => {
                  setShowCreateModal(true);
                }}>
                신규 쿠폰 생성
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 320px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((coupon) => coupon?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={couponsQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DiscountCouponsPage;
